import React, { forwardRef, useState } from 'react';
import cn from 'classnames';

/** Checkmark icon matching design (white path on checked fill) */
const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="100%"
    height="100%"
    viewBox="2 2 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.2929 7.29289C13.6834 6.90237 14.3166 6.90237 14.7071 7.29289C15.0676 7.65338 15.0953 8.22061 14.7903 8.6129L14.7071 8.70711L9.70711 13.7071C9.34662 14.0676 8.77939 14.0953 8.3871 13.7903L8.29289 13.7071L5.29289 10.7071C4.90237 10.3166 4.90237 9.68342 5.29289 9.29289C5.65338 8.93241 6.22061 8.90468 6.6129 9.2097L6.70711 9.29289L9 11.585L13.2929 7.29289Z"
      fill="currentColor"
    />
  </svg>
);

export type CheckboxSize = 'xs' | 'small' | 'medium' | 'large' | 'xl';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Checkbox size – medium is the default/normal size */
  size?: CheckboxSize;
  /** Label text shown next to the checkbox */
  label?: React.ReactNode;
  /** Error state */
  error?: boolean;
  /** Marks this option as a correct answer. Use with checked for the selected-correct quiz state. */
  correct?: boolean;
  /** Marks this option as a correct answer the user did not select (missed). Applies a neutral/grayish background instead of the selected-correct teal. */
  missed?: boolean;
  /** Optional description or error message below */
  helperText?: React.ReactNode;
  className?: string;
  /** Wrapper className (e.g. for layout) */
  wrapperClassName?: string;
  /** Label element className (e.g. w-full for full-width rows) */
  labelClassName?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      size = 'medium',
      label,
      error = false,
      correct = false,
      missed = false,
      disabled = false,
      checked,
      defaultChecked,
      helperText,
      className,
      wrapperClassName,
      labelClassName,
      id: idProp,
      onChange,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const id = idProp ?? `checkbox-${generatedId}`;
    const isControlled = checked !== undefined;
    const [uncontrolledChecked, setUncontrolledChecked] = useState(Boolean(defaultChecked));
    const isChecked = isControlled ? checked : uncontrolledChecked;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) setUncontrolledChecked(e.target.checked);
      onChange?.(e);
    };

    const sizeClasses = {
      xs: 'h-3 w-3',
      small: 'h-4 w-4',
      medium: 'h-5 w-5',
      large: 'h-6 w-6',
      xl: 'h-7 w-7',
    };
    const iconSizeClasses = {
      xs: 'h-3 w-3',
      small: 'h-4 w-4',
      medium: 'h-5 w-5',
      large: 'h-6 w-6',
      xl: 'h-7 w-7',
    };
    const labelSizeClasses = {
      xs: 'text-ds-caption',
      small: 'text-ds-text-s',
      medium: 'text-ds-text',
      large: 'text-ds-text',
      xl: 'text-ds-text-l',
    };
    const helperSizeClasses = {
      xs: 'text-ds-caption-s',
      small: 'text-ds-caption',
      medium: 'text-ds-caption',
      large: 'text-ds-text-s',
      xl: 'text-ds-text-s',
    };

    return (
      <div className={cn('inline-flex flex-col gap-1', wrapperClassName)}>
        <label
          htmlFor={id}
          className={cn(
            'group inline-flex cursor-pointer items-center gap-3 align-top rounded-lg',
            // Quiz feedback: add padding + background when in a feedback state
            isChecked &&
              correct &&
              !missed &&
              !disabled &&
              'py-1 pl-1.5 pr-3 bg-ds-bg-surface-soft-5',
            isChecked && missed && !disabled && 'py-1 pl-1.5 pr-3 bg-ds-bg-surface-soft-4',
            isChecked && error && !disabled && 'py-1 pl-1.5 pr-3 bg-ds-bg-feedback-soft-error',
            disabled && 'cursor-not-allowed opacity-60',
            !disabled && 'cursor-pointer',
            labelClassName,
          )}
        >
          <span className="relative inline-flex shrink-0">
            <input
              ref={ref}
              type="checkbox"
              id={id}
              checked={isControlled ? checked : undefined}
              defaultChecked={!isControlled ? defaultChecked : undefined}
              disabled={disabled}
              aria-invalid={error}
              aria-describedby={helperText ? `${id}-helper` : undefined}
              className="peer sr-only"
              onChange={handleChange}
              {...props}
            />
            <span
              className={cn(
                'inline-flex shrink-0 items-center justify-center border transition-colors duration-200',
                sizeClasses[size],
                'rounded-[var(--radius-ds-1)]',
                'peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-ds-aqua-300 peer-focus-visible:ring-offset-2',
                // Default
                !disabled &&
                  !error &&
                  !isChecked && [
                    'border-ds-border-3 bg-ds-bg-surface-4',
                    'group-hover:border-ds-border-2',
                  ],
                // Checked
                !disabled &&
                  !error &&
                  isChecked && ['border-transparent bg-ds-bg-surface-2 text-ds-text-inverse-1'],
                // Disabled unchecked
                disabled &&
                  !isChecked && [
                    'border-ds-border-soft-3 bg-ds-bg-surface-soft-3 text-ds-text-inverse-1',
                  ],
                // Disabled checked
                disabled &&
                  isChecked && [
                    'border-transparent bg-ds-bg-surface-2 text-ds-text-inverse-1 opacity-60',
                  ],
                // Error unchecked
                error &&
                  !isChecked && [
                    'border-ds-icon-feedback-error bg-ds-bg-feedback-soft-error text-ds-text-feedback-error',
                  ],
                // Error checked – red fill to clearly mark wrong answer
                error &&
                  isChecked && [
                    'border-transparent bg-ds-bg-feedback-error text-ds-text-inverse-1',
                  ],
                className,
              )}
              aria-hidden
            >
              {isChecked && <CheckIcon className={cn(iconSizeClasses[size], 'text-current')} />}
            </span>
          </span>
          {label != null &&
            (missed ? (
              <span className={cn(labelSizeClasses[size], 'text-ds-text-1 select-none w-full')}>
                {label}
              </span>
            ) : (
              <span className={cn(labelSizeClasses[size], 'text-ds-text-1 select-none')}>
                {label}
              </span>
            ))}
        </label>
        {helperText != null && (
          <span
            id={`${id}-helper`}
            className={cn(
              helperSizeClasses[size],
              'pl-8',
              error ? 'text-ds-text-feedback-error' : 'text-ds-text-3',
            )}
          >
            {helperText}
          </span>
        )}
      </div>
    );
  },
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
